import React, { useState } from 'react';
import { Star, Send, CheckCircle } from 'lucide-react';
import { leadsApi } from '../../services/api'; // We'll add submitTestimonial to this
import axios from 'axios';
import { useModal } from '../ui/ModalProvider';

const TestimonialSubmission = () => {
    const { showAlert } = useModal();
    const [formData, setFormData] = useState({
        name: '',
        company: '',
        content: '',
        rating: 5,
        avatar: '',
        videoUrl: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);

    const handleVideoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Visual validation
        if (file.size > 50 * 1024 * 1024) { // 50MB limit
            showAlert('File is too large. Please upload a video under 50MB.');
            return;
        }

        setUploadingVideo(true);
        setUploadProgress(10);

        try {
            const uploadForm = new FormData();
            uploadForm.append('file', file);

            const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';
            const response = await axios.post(`${backendUrl}/api/assets/upload`, uploadForm, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(progress);
                }
            });

            if (response.data.success) {
                setFormData(prev => ({ ...prev, videoUrl: response.data.url }));
                toast({
                    title: "Video Uploaded",
                    description: "Your video testimonial has been attached.",
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            showAlert('Video upload failed. Please try a different file.');
        } finally {
            setUploadingVideo(false);
            setUploadProgress(0);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';
            await axios.post(`${backendUrl}/api/testimonials/submit`, formData);
            setSubmitted(true);
        } catch (error) {
            console.error('Error submitting testimonial:', error);
            showAlert('Failed to submit testimonial. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4 font-sans">
                <div className="glass-morphism p-8 rounded-2xl max-w-md w-full text-center space-y-6">
                    <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto text-green-500">
                        <CheckCircle size={48} />
                    </div>
                    <h2 className="text-3xl font-bold text-silver">Thank You!</h2>
                    <p className="text-gray-400">
                        Your testimonial has been submitted successfully. It will be reviewed by our team and published soon.
                    </p>
                    <button
                        onClick={() => setSubmitted(false)}
                        className="text-silver hover:text-white transition-colors"
                    >
                        Submit another one
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] py-20 px-4 font-sans">
            <div className="max-w-2xl mx-auto space-y-12">
                <div className="text-center space-y-4">
                    <h1 className="text-4xl md:text-5xl font-bold text-silver">Share Your Experience</h1>
                    <p className="text-gray-400 font-light">Your feedback helps us grow and helps others understand the value we bring.</p>
                </div>

                <form onSubmit={handleSubmit} className="glass-morphism p-8 rounded-2xl space-y-6 border border-white/5">
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400 uppercase tracking-widest text-[10px]">Full Name</label>
                            <input
                                required
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-silver focus:outline-none focus:border-silver/50 transition-colors placeholder:text-gray-600"
                                placeholder="John Doe"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400 uppercase tracking-widest text-[10px]">Company / Role</label>
                            <input
                                type="text"
                                value={formData.company}
                                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-silver focus:outline-none focus:border-silver/50 transition-colors placeholder:text-gray-600"
                                placeholder="CEO at TechCo"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-400 uppercase tracking-widest text-[10px]">Rating</label>
                        <div className="flex gap-2">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setFormData({ ...formData, rating: star })}
                                    className={`p-1 transition-all duration-300 ${formData.rating >= star ? 'text-yellow-500 scale-110' : 'text-gray-700'}`}
                                >
                                    <Star size={24} fill={formData.rating >= star ? 'currentColor' : 'none'} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-400 uppercase tracking-widest text-[10px]">Your Testimonial</label>
                        <textarea
                            required
                            rows={5}
                            value={formData.content}
                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-silver focus:outline-none focus:border-silver/50 transition-colors resize-none placeholder:text-gray-600"
                            placeholder="Tell us about your experience working with Hara-XY..."
                        />
                    </div>

                    <div className="space-y-2 pt-2">
                        <label className="text-sm font-medium text-gray-400 uppercase tracking-widest text-[10px]">Video Testimonial (Optional)</label>
                        <div className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-300 flex flex-col items-center justify-center gap-3 ${formData.videoUrl ? 'border-green-500/50 bg-green-500/5' : 'border-white/10 hover:border-white/20'}`}>
                            {uploadingVideo ? (
                                <div className="w-full space-y-3 px-4">
                                    <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-silver transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                    <p className="text-xs text-center text-gray-400">Uploading: {uploadProgress}%</p>
                                </div>
                            ) : formData.videoUrl ? (
                                <>
                                    <div className="text-green-500 flex items-center gap-2">
                                        <CheckCircle size={20} />
                                        <span className="text-sm font-medium">Video Attached Successfully</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setFormData(prev => ({ ...prev, videoUrl: '' }))}
                                        className="text-xs text-red-500 hover:underline"
                                    >
                                        Remove and re-upload
                                    </button>
                                </>
                            ) : (
                                <>
                                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400">
                                        <Send size={20} className="rotate-90" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-silver text-sm">Drop your video here or click to browse</p>
                                        <p className="text-gray-500 text-xs mt-1">MP4, WEBM preferred. Max 50MB.</p>
                                    </div>
                                </>
                            )}
                            <input
                                type="file"
                                accept="video/*"
                                onChange={handleVideoUpload}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                                disabled={uploadingVideo}
                            />
                        </div>
                    </div>

                    <button
                        disabled={loading}
                        type="submit"
                        className="w-full bg-silver text-black font-bold py-4 rounded-lg hover:bg-white transition-colors flex items-center justify-center gap-2 group disabled:opacity-50"
                    >
                        {loading ? 'Submitting...' : 'Submit Testimonial'}
                        {!loading && <Send size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default TestimonialSubmission;
